import { MatchupDriver, MatchupHeadToHead, SimDriverResult } from '../services/api';

/**
 * Book mode: our probabilities translated into the prices a sportsbook would typically post.
 *
 * On the log-odds scale a book's price is roughly a straight line of ours:
 *   logit(book) = a + k × logit(ours)
 * k < 1 flattens the field (favorites come down, longshots come up) and a adds the book's margin, so a market's
 * book-style probabilities add up to more than 100% the way a real board does.
 *
 * Win / top 3 / top 5 / top 10 were fitted on a full sportsbook board (Las Vegas, 2026-10-02) against the production
 * model with the recent-form blend (analysis log entries 15 and 16; refit 2026-10-04).
 * Head-to-heads and best-of-group use the top-10 slope and the matchup margin real boards show: about 7% on a pair
 * (−115 / −115 at even odds; six scanned matchups from two boards ran 6.8–7.6%, analysis log entry 42; it was a
 * textbook −110 / −110 before).
 */
export type BookMarket = 'win' | 'top3' | 'top5' | 'top10' | 'h2h';

export const BOOK_SHAPE: Record<BookMarket, { a: number; k: number }> = {
  win: { a: -0.51, k: 0.66 },
  top3: { a: -0.04, k: 0.75 },
  top5: { a: 0.19, k: 0.83 },
  top10: { a: 0.42, k: 0.96 },
  h2h: { a: 0.14, k: 0.96 },
};

const EPS = 1e-4;
const logit = (p: number) => Math.log(p / (1 - p));
const sigmoid = (x: number) => 1 / (1 + Math.exp(-x));

/** The book-style (margin-included) probability for our probability p in a market. */
export const bookProb = (p: number, market: BookMarket): number => {
  if (!(p > 0)) return 0; // never happened in the simulation: no line
  const { a, k } = BOOK_SHAPE[market];
  return sigmoid(a + k * logit(Math.min(Math.max(p, EPS), 1 - EPS)));
};

export const bookMarketForTopN = (n: number): BookMarket =>
  n === 1 ? 'win' : n <= 3 ? 'top3' : n <= 5 ? 'top5' : 'top10';

/** A simulator row with its probabilities shown book-style (finish stats are unchanged). */
export const bookDriver = (d: SimDriverResult): SimDriverResult => ({
  ...d,
  winProb: bookProb(d.winProb, 'win'),
  top3Prob: bookProb(d.top3Prob, 'top3'),
  top5Prob: bookProb(d.top5Prob, 'top5'),
  top10Prob: bookProb(d.top10Prob, 'top10'),
});

/** A matchup driver with its probabilities shown book-style. */
export const bookMatchupDriver = (d: MatchupDriver): MatchupDriver => ({
  ...d,
  groupWinProb: bookProb(d.groupWinProb, 'h2h'),
  winProb: bookProb(d.winProb, 'win'),
  top5Prob: bookProb(d.top5Prob, 'top5'),
  top10Prob: bookProb(d.top10Prob, 'top10'),
});

export const bookHeadToHead = (h: MatchupHeadToHead): MatchupHeadToHead => ({
  ...h,
  probAAhead: bookProb(h.probAAhead, 'h2h'),
});

/**
 * Our probability at and above which books typically price a market below us (where a + k × logit(p) = logit(p)), or
 * null when that only happens on near-certain bets (above 90%). Below it, a typical book price is shorter than ours.
 */
export const bookCrossover = (market: BookMarket): number | null => {
  const { a, k } = BOOK_SHAPE[market];
  if (k >= 1) return null;
  const p = sigmoid(a / (1 - k));
  return p <= 0.9 ? p : null;
};

/** A parlay leg as the gap breakdown needs it: its kind and our probability. */
export interface GapLeg {
  kind: 'top_n' | 'not_top_n' | 'ahead_of';
  n?: number;
  prob: number;
}

/**
 * The typical book price for one parlay leg, or null when books don't usually post that side ("not top N" fades).
 * Matchups and best-of-group legs use the head-to-head shape.
 */
export const bookLegProb = (leg: GapLeg): number | null => {
  if (leg.kind === 'top_n') return bookProb(leg.prob, bookMarketForTopN(leg.n ?? 1));
  if (leg.kind === 'ahead_of') return bookProb(leg.prob, 'h2h');
  return null;
};

export interface ParlayGap {
  /** Each leg's typical book price and our probability ÷ that price. */
  legs: { book: number; ratio: number }[];
  /** Our legs ÷ typical book legs, multiplied over the slip (independent prices on both sides). */
  legsRatio: number;
  /** What a book would typically post for the parlay: its leg prices multiplied, times our correlation effect. */
  typical: number;
}

/**
 * Why a parlay is or isn't a value, split in two: what the legs cost at typical book prices (multiplied, so every leg
 * a book prices above us compounds), and how a book's actual price compares with that typical price. Our joint
 * probability ÷ the book's implied = legsRatio × (typical ÷ implied), exactly. Null when any leg has no typical price.
 */
export const parlayGap = (legs: GapLeg[], joint: number, independent: number): ParlayGap | null => {
  if (legs.length === 0 || !(joint > 0) || !(independent > 0)) return null;
  const priced = legs.map((l) => {
    const book = bookLegProb(l);
    return book !== null && book > 0 && l.prob > 0 ? { book, ratio: l.prob / book } : null;
  });
  if (priced.some((p) => p === null)) return null;
  const rows = priced as { book: number; ratio: number }[];
  const bookIndependent = rows.reduce((acc, r) => acc * r.book, 1);
  const typical = Math.min(bookIndependent * (joint / independent), 0.999);
  return { legs: rows, legsRatio: independent / bookIndependent, typical };
};
